import React, { useEffect, useState } from "react";
import { Alert, Container, TableHead } from "@mui/material";
import { useJwtStorage, useLocalStorage } from "../../hooks/useLocalStorage";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Grid from "@mui/material/Grid";

import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import {ReactComponent as DeleteIcon} from "../../icons/Delete24.svg";
import IconButton from "@mui/material/IconButton";

import dayjs from "dayjs";
import { MobileDateRangePicker } from "@mui/x-date-pickers-pro/MobileDateRangePicker";

import DrawerAppBar from "../../components/DrawerAppBar";
import Title from "../../components/Title";
import ProgressBar from "../../components/ProgressBar";

import {
  getCommServiceTypes,
  getRestrictedEmails,
  getPresignedCommServiceUrl,
  saveCommService,
} from "../../services/CommServiceService";

import { doService } from "../../services/ServiceHelper";
import { httpUpload } from "../../services/AbstractService";
import UploadFileDialog from "../../components/faculty/UploadFileDialog";
import {
  StripedTableCell,
  StripedTableRow,
} from "../../components/StripedTable";
import TableRowNoResults from "../../components/TableRowNoResults";
import { showError, showSuccess } from "../../components/ATPDialogs";
import { isValidEmail } from "../../services/StringService";
import isEmpty from "lodash-es/isEmpty";
import TextAlert from "../../components/TextAlert";

const AddService = () => {
  const [_school] = useLocalStorage("Auth", "school", null);
  const [_jwt] = useJwtStorage();

  const [showProgress, setShowProgress] = useState(false);

  const [_description, setDescription] = useState("");

  const [_hours, setHours] = useState(0);
  const [_minutes, setMinutes] = useState(0);
  const [_type, setType] = useState("XX");
  const [_types, setTypes] = useState([]);
  const [_files, setFiles] = useState([]);
  const [_uploadDialogOpen, setUploadDialogOpen] = React.useState(false);
  const [_dateRange, setDateRange] = useState([dayjs(), dayjs()]);
  const [_verifierEmail, setVerifierEmail] = useState("");
  const [_shaCode, setShaCode] = useState(null);
  const [_restrictedEmails, setRestrictedEmails] = useState([]);

  const initTypes = () => {
    setShowProgress(true);

    doService(getCommServiceTypes(_school, _jwt), (result) => {
      setShowProgress(false);

      // remove types not available to students
      const types = [];
      for (const t of result.data) {
        if (t.showToStudent === "Y") {
          types.push(t);
        }
      }

      setTypes(types);
    });
  };

  const initRestrictedEmails = () => {
    setShowProgress(true);

    doService(getRestrictedEmails(_school, _jwt), (results) => {
      setShowProgress(false);

      const emailDomains = [];
      for (const result of results.data) {
        emailDomains.push(result.emailDomain);
      }

      setRestrictedEmails(emailDomains);
    });
  };

  useEffect(() => {
    initTypes();
    initRestrictedEmails();
  }, []);

  const saveService = (verifyWith) => {
    setShowProgress(true);

    let contentType = "";
    let savedFilename = "";

    if (verifyWith === "FI" || verifyWith === "ALL") {
      const file = _files[0];

      doService(
        getPresignedCommServiceUrl(_school, _jwt, file.name),
        (result) => {
          const signedUrl = result.data.url;
          contentType = result.data.content_type;
          savedFilename = result.data.saved_filename;

          // upload file
          httpUpload(signedUrl, file, contentType).then((result) => {
            // saves the service file
            doSaveCommService(
              verifyWith,
              file.name,
              savedFilename,
              file.size,
              contentType,
              _shaCode
            );
          });
        }
      );
    } else if (verifyWith === "VE" || verifyWith === "ALL") {
      doSaveCommService(verifyWith, "", "", 0, "");
    }
  };

  const doSaveCommService = (
    verifyWith,
    filename,
    savedFilename,
    fileSize,
    contentType,
    shaCode
  ) => {
    const dateStart = _dateRange[0].format("YYYY-MM-DD");
    const dateEnd = _dateRange[1].format("YYYY-MM-DD");

    saveCommService(
      _school,
      _jwt,
      _type,
      dateStart,
      dateEnd,
      _description,
      _hours,
      _minutes,
      verifyWith,
      _verifierEmail,
      filename,
      savedFilename,
      fileSize,
      contentType,
      shaCode
    ).then((result) => {
      setShowProgress(false);

      if (result.detail) {
        alert("Unable to Add Service Op: " + result.detail);
        return;
      }

      showSuccess("Service Added!");
      setFiles([]);
      setType("XX");
      setDescription("");
      setHours(0);
      setMinutes(0);
      setDateRange([dayjs(), dayjs()]);
      setVerifierEmail("");
    });
  };

  const handleChangeType = (event) => {
    const eid = event.target.value;
    setType(eid);
  };

  const handleChangeHours = (event) => {
    setHours(event.target.value);
  };

  const handleChangeMinutes = (event) => {
    setMinutes(event.target.value);
  };

  const handleSubmit = () => {
    let verifyWith = "";

    if (_type === "XX") {
      showError("Unable to Add Service Op", "Please select a Service Type.");
      return;
    }

    if (_description === "") {
      showError("Unable to Add Service Op", "Please enter a Description.");
      return;
    }

    if (_hours === "XX") {
      showError("Unable to Add Service Op", "Please select Hours.");
      return;
    }

    if (_minutes === "XX") {
      showError("Unable to Add Service Op", "Please select Minutes.");
      return;
    }

    const hours = parseInt(_hours);
    const minutes = parseInt(_minutes);
    if (hours === 0 && minutes === 0) {
      showError("Unable to Add Service Op", "Please select Hours and Minutes.");
      return;
    }

    if (!isEmpty(_verifierEmail) && _files.length > 0) {
      verifyWith = "ALL";
    } else if (!isEmpty(_verifierEmail)) {
      verifyWith = "VE";
    } else if (_files.length > 0) {
      verifyWith = "FI";
    }

    if (verifyWith === "") {
      showError(
        "Unable to Add Service Op",
        "Please enter a Verifier Email or select a Service File."
      );
      return;
    }

    if (verifyWith === "VE") {
      if (!isValidEmail(_verifierEmail)) {
        showError(
          "Unable to Add Service Op",
          "Please enter a valid email address."
        );
        return;
      }

      for (const emailDomain of _restrictedEmails) {
        if (_verifierEmail.toLowerCase().endsWith(emailDomain)) {
          showError(
            "Unable to Add Service Op",
            `Email Address ${emailDomain} is not permitted.`
          );
          return;
        }
      }
    }

    // validate against previous pass
    saveService(verifyWith);
  };

  const onChangeUploadFiles = (files) => {};

  const generateSha = async (file, algo = "SHA-256") => {
    const buf = await file.arrayBuffer();
    const digest = await crypto.subtle.digest(algo, buf);
    return [...new Uint8Array(digest)]
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  };

  const onClickUploadDone = async (files) => {
    if (files == null || files.length === 0) {
      alert("Please select a file.");
      return;
    }

    if (files[0].size > 5000000) {
      alert("File must be less than 5 megabytes.");
      return;
    }

    const shaCode = await generateSha(files[0]);
    console.log(shaCode);
    setShaCode(shaCode);

    setFiles(files);
    setUploadDialogOpen(false);
  };

  const onClickImport = () => {
    setUploadDialogOpen(true);
  };

  const handleClickRemoveFile = () => {
    setFiles([]);
  };

  return (
    <>
      <DrawerAppBar />

      <Container sx={{ mt: 1 }}>
        <Title text="Add Community Service Op" showBack={true} />
        <ProgressBar showProgress={showProgress} />

        <TextAlert type="PDF" text="Click here for instructions to add a service op." url="/cheat-sheets/student-create-service-op" />
        <br/>
        <Alert severity="warning">
          Your name may be included in the email that is sent to the verifier
          email address.
        </Alert>

        <InputLabel sx={{ mt: 1 }}>* Service Date Range</InputLabel>
        <MobileDateRangePicker
          slotProps={{ textField: { size: "small" } }}
          value={_dateRange}
          sx={{ mt: 1 }}
          onChange={(newValue) => {
            setDateRange(newValue);
          }}
        />

        <InputLabel sx={{ mt: 1 }}>* Service Type:</InputLabel>
        <Select
          onChange={handleChangeType}
          value={_type}
          fullWidth
          size="small"
        >
          <MenuItem value="XX">-- SELECT --</MenuItem>
          {_types.map((type) => (
            <MenuItem key={type.eid} value={type.eid}>
              {type.name}
            </MenuItem>
          ))}
        </Select>

        <InputLabel sx={{ mt: 1 }}>* Description</InputLabel>
        <TextField
          margin="dense"
          type="text"
          fullWidth
          size="small"
          onChange={(evt) => setDescription(evt.target.value)}
          placeholder="Required Field"
          value={_description}
          sx={{ mt: 0 }}
        />

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <InputLabel sx={{ mt: 1 }}>* Hours Spent</InputLabel>
            <Select
              onChange={handleChangeHours}
              value={_hours}
              fullWidth
              size="small"
            >
              <MenuItem value="XX">-- HOURS --</MenuItem>
              {Array.from(Array(41), (e, i) => {
                return (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                );
              })}
            </Select>
          </Grid>
          <Grid item xs={6}>
            <InputLabel sx={{ mt: 1 }}>* Minutes Spent</InputLabel>

            <Select
              onChange={handleChangeMinutes}
              value={_minutes}
              fullWidth
              size="small"
            >
              <MenuItem value="XX">-- MINUTES --</MenuItem>
              <MenuItem value="0">0</MenuItem>
              <MenuItem value="15">15</MenuItem>
              <MenuItem value="30">30</MenuItem>
              <MenuItem value="45">45</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={6}>
            <InputLabel sx={{ mt: -1 }}>Verifier Email</InputLabel>
            <TextField
              margin="dense"
              type="text"
              fullWidth
              size="small"
              onChange={(evt) => setVerifierEmail(evt.target.value)}
              placeholder="Required Field"
              value={_verifierEmail}
              sx={{ mt: 1 }}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel sx={{ mt: -1 }}>Service File</InputLabel>
            <UploadFileDialog
              uploadDialogOpen={_uploadDialogOpen}
              setUploadDialogOpen={setUploadDialogOpen}
              fileTypes={[
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/pdf",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "image/heif",
                "image/jpeg",
                "image/png",
              ]}
              onChangeUploadFiles={onChangeUploadFiles}
              onClickDone={onClickUploadDone}
            />

            <Button onClick={onClickImport} sx={{ mt: 1 }} variant="outlined">
              Select File
            </Button>
          </Grid>
        </Grid>

        {_files.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2, mb: 1 }}>
            <Table size="small">
              <TableHead>
                <StripedTableRow>
                  <StripedTableCell>File Name</StripedTableCell>
                  <StripedTableCell width={50} align="center">
                    Delete?
                  </StripedTableCell>
                </StripedTableRow>
              </TableHead>
              <TableBody>
                <TableRowNoResults data={_files} />
                {_files.map((file) => (
                  <StripedTableRow>
                    <StripedTableCell>{file.name}</StripedTableCell>
                    <StripedTableCell align="center">
                      <IconButton
                        edge="end"
                        aria-label="remove file"
                        title="remove file"
                        onClick={() => handleClickRemoveFile()}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </StripedTableCell>
                  </StripedTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Button
          onClick={handleSubmit}
          sx={{ mt: 1 }}
          variant="contained"
          fullWidth
        >
          Add Service Op
        </Button>
      </Container>
    </>
  );
};

export default AddService;
